import { useMemo, useState } from "react";
import {
  ArrowDownAZ,
  Building2,
  ExternalLink,
  Link2,
  Search,
  Shield,
  TrendingUp,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toggle } from "@/components/ui/toggle";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  useCompanies,
  useNewsSources,
  useSourceArticles,
} from "@/hooks/api";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useToggleState } from "@/hooks/useToggleState";
import { cn } from "@/lib/utils";
import { formatInt, formatRelativeHours } from "@/lib/format";
import { ScoreBar } from "@/components/views/companies/ArticlesTable";
import type { ArticleOut, CompanyListItemOut } from "@/api/client";

type Order = null | "asc" | "desc";

export function NewsMain({ sourceId }: { sourceId: number | null }) {
  const isMobile = useIsMobile();
  const [titleOrder, cycleTitle] = useToggleState<Order>([null, "asc", "desc"]);
  const [trustOrder, cycleTrust] = useToggleState<Order>([null, "desc", "asc"]);
  const [predOrder, cyclePred] = useToggleState<Order>([null, "desc", "asc"]);
  const [keyword, setKeyword] = useState("");
  const [companyFilter, setCompanyFilter] = useState<{ id: number; symbol: string } | null>(null);
  const [companyPopoverOpen, setCompanyPopoverOpen] = useState(false);

  const { data: sources } = useNewsSources();
  const provider = sources?.find((s) => s.id === sourceId);
  const { data: articles, isLoading } = useSourceArticles(sourceId, {
    q: keyword || undefined,
    companyId: companyFilter?.id,
  });
  const { data: companies } = useCompanies();

  const sorted = useMemo(() => {
    const arr = [...(articles ?? [])];
    arr.sort((a, b) => {
      let cmp = 0;
      if (predOrder) {
        const av = a.importance_score ?? -1;
        const bv = b.importance_score ?? -1;
        cmp = predOrder === "desc" ? bv - av : av - bv;
        if (cmp !== 0) return cmp;
      }
      if (trustOrder) {
        const av = a.trust_score ?? -1;
        const bv = b.trust_score ?? -1;
        cmp = trustOrder === "desc" ? bv - av : av - bv;
        if (cmp !== 0) return cmp;
      }
      if (titleOrder) {
        const at = a.title ?? "";
        const bt = b.title ?? "";
        cmp = titleOrder === "asc" ? at.localeCompare(bt) : bt.localeCompare(at);
      }
      return cmp;
    });
    return arr;
  }, [articles, predOrder, trustOrder, titleOrder]);

  if (sourceId === null) {
    return (
      <div className="p-8 text-muted-foreground text-sm">
        Pick a content source from the sidebar to see its articles.
      </div>
    );
  }
  if (!provider) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-3 md:px-4 py-3 border-b border-border">
        <div className="flex flex-col gap-2 md:flex-row md:items-baseline md:justify-between md:gap-3">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="font-bold text-[16px]">{provider.name}</div>
              <Badge variant="outline">{provider.kind}</Badge>
            </div>
            <div className="text-[10px] text-muted-foreground font-mono mt-0.5">
              Last scraped {formatRelativeHours(provider.last_scraped_at)} ·{" "}
              {formatInt(provider.scraped_count)} articles total
            </div>
          </div>
          <Button variant="outline" size="sm" disabled className="self-start md:self-auto">
            <Link2 className="h-3 w-3" /> Re-scrape now
          </Button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-3">
          <MetricCard label="Scraped" value={formatInt(provider.scraped_count)} mono />
          <MetricCard
            label="Last scrape"
            value={formatRelativeHours(provider.last_scraped_at)}
            mono
          />
          <MetricCard
            label="Importance"
            value={provider.importance_score?.toString() ?? "—"}
            pos={
              provider.importance_score === null || provider.importance_score === undefined
                ? undefined
                : provider.importance_score > 70
            }
          />
          <MetricCard
            label="Relevance"
            value={provider.relevance_score?.toString() ?? "—"}
            pos={
              provider.relevance_score === null || provider.relevance_score === undefined
                ? undefined
                : provider.relevance_score > 70
            }
          />
          <MetricCard
            label="Pred. impact"
            value={provider.prediction_importance_score?.toString() ?? "—"}
            pos={
              provider.prediction_importance_score === null ||
              provider.prediction_importance_score === undefined
                ? undefined
                : provider.prediction_importance_score > 70
            }
          />
        </div>
      </div>

      <div className="px-3 py-2 border-b border-border bg-muted/20 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          <FilterChip
            active={!!titleOrder}
            state={titleOrder === "asc" ? "A↓Z" : titleOrder === "desc" ? "Z↓A" : ""}
            onClick={cycleTitle}
            title="Sort by title"
          >
            <ArrowDownAZ className="h-3 w-3" />
          </FilterChip>
          <FilterChip
            active={!!trustOrder}
            state={trustOrder === "desc" ? "↓" : trustOrder === "asc" ? "↑" : ""}
            onClick={cycleTrust}
            title="Sort by trust"
          >
            <Shield className="h-3 w-3" />
          </FilterChip>
          <FilterChip
            active={!!predOrder}
            state={predOrder === "desc" ? "↓" : predOrder === "asc" ? "↑" : ""}
            onClick={cyclePred}
            title="Sort by prediction importance"
          >
            <TrendingUp className="h-3 w-3" />
          </FilterChip>
        </div>
        <div className="hidden md:block h-4 w-px bg-border" />
        <div className="relative w-full md:w-64 order-last md:order-none basis-full md:basis-auto">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
          <Input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="Search article keywords..."
            className="pl-7 h-6 text-[11px]"
          />
        </div>
        <CompanyFilterControl
          isMobile={isMobile}
          open={companyPopoverOpen}
          setOpen={setCompanyPopoverOpen}
          companyFilter={companyFilter}
          setCompanyFilter={setCompanyFilter}
          companies={companies ?? []}
        />
        <div className="ml-auto text-[10px] text-muted-foreground font-mono">
          {sorted.length} articles
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">Loading…</div>
        ) : sorted.length === 0 ? (
          <div className="px-3 py-6 text-center text-[12px] text-muted-foreground">
            No articles match the current filters.
          </div>
        ) : (
          <>
            <div className="md:hidden">
              {sorted.map((a) => (
                <NewsArticleMobileCard key={a.id} a={a} />
              ))}
            </div>
            <Table className="hidden md:table text-[11px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="h-7 px-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30" style={{ width: 100 }}>
                    Date
                  </TableHead>
                  <TableHead className="h-7 px-1 bg-muted/30" style={{ width: 24 }} />
                  <TableHead className="h-7 px-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    Title
                  </TableHead>
                  <TableHead className="h-7 px-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30">
                    Summary
                  </TableHead>
                  <TableHead className="h-7 px-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30" style={{ width: 100 }}>
                    Importance
                  </TableHead>
                  <TableHead className="h-7 px-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/30" style={{ width: 60 }}>
                    Trust
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((a) => (
                  <TableRow key={a.id} className="hover:bg-accent/40">
                    <TableCell className="py-1.5 font-mono text-muted-foreground align-top">
                      {a.published_at ? a.published_at.slice(0, 10) : "—"}
                    </TableCell>
                    <TableCell className="py-1.5 px-1 align-top">
                      {a.url ? (
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center text-muted-foreground hover:text-foreground"
                          title="Open article in new tab"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : null}
                    </TableCell>
                    <TableCell className="py-1.5 font-medium align-top">{a.title ?? "—"}</TableCell>
                    <TableCell className="py-1.5 text-muted-foreground max-w-[380px] truncate align-top">
                      {a.summary ?? "—"}
                    </TableCell>
                    <TableCell className="py-1.5 align-top">
                      <ScoreBar v={a.importance_score} />
                    </TableCell>
                    <TableCell className="py-1.5 font-mono tabular-nums align-top">
                      {a.trust_score ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </div>
    </div>
  );
}

function CompanyFilterControl({
  isMobile,
  open,
  setOpen,
  companyFilter,
  setCompanyFilter,
  companies,
}: {
  isMobile: boolean;
  open: boolean;
  setOpen: (v: boolean) => void;
  companyFilter: { id: number; symbol: string } | null;
  setCompanyFilter: (v: { id: number; symbol: string } | null) => void;
  companies: CompanyListItemOut[];
}) {
  const trigger = (
    <button
      type="button"
      className="relative w-full md:w-64 h-6 rounded-[4px] border border-border bg-background pl-7 pr-2 text-[11px] text-left flex items-center"
    >
      <Building2 className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
      {companyFilter ? (
        <span className="truncate">
          <span className="font-mono font-semibold">{companyFilter.symbol}</span>
        </span>
      ) : (
        <span className="text-muted-foreground">Filter by company…</span>
      )}
      {companyFilter && (
        <X
          className="ml-auto h-3 w-3 text-muted-foreground hover:text-foreground"
          onClick={(e) => {
            e.stopPropagation();
            setCompanyFilter(null);
          }}
        />
      )}
    </button>
  );

  const commandBody = (
    <Command>
      <CommandInput placeholder="Type a ticker or name…" />
      <CommandList>
        <CommandEmpty>No company found.</CommandEmpty>
        <CommandGroup>
          {companies.slice(0, 50).map((c) => (
            <CommandItem
              key={c.id}
              value={`${c.primary_symbol ?? ""} ${c.name}`}
              onSelect={() => {
                setCompanyFilter({ id: c.id, symbol: c.primary_symbol ?? c.name });
                setOpen(false);
              }}
            >
              <span className="font-mono font-semibold w-12">{c.primary_symbol ?? "—"}</span>
              <span className="text-muted-foreground truncate">{c.name}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>{trigger}</SheetTrigger>
        <SheetContent side="bottom" className="max-h-[80dvh] p-0 flex flex-col">
          <SheetHeader className="px-3 py-2 border-b border-border">
            <SheetTitle className="text-[12px]">Filter by company</SheetTitle>
          </SheetHeader>
          <div className="flex-1 min-h-0 overflow-hidden">{commandBody}</div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        {commandBody}
      </PopoverContent>
    </Popover>
  );
}

function NewsArticleMobileCard({ a }: { a: ArticleOut }) {
  const inner = (
    <div className="block px-3 py-2 border-b border-border/60 min-h-[44px] hover:bg-accent/40">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[12px] font-medium leading-snug min-w-0 truncate">
          {a.title ?? "—"}
        </div>
        <div className="font-mono text-[10px] tabular-nums text-muted-foreground shrink-0">
          {a.published_at ? a.published_at.slice(5, 10) : "—"}
        </div>
      </div>
      <div className="flex items-baseline justify-between gap-2 mt-0.5 text-[10px] text-muted-foreground font-mono">
        <span className="truncate uppercase tracking-wider">{a.source_name}</span>
        <span className="shrink-0">
          imp {a.importance_score ?? "—"} · trust {a.trust_score ?? "—"}
        </span>
      </div>
      {a.summary && (
        <div className="text-[10px] text-muted-foreground mt-1 line-clamp-2">{a.summary}</div>
      )}
    </div>
  );
  if (a.url) {
    return (
      <a href={a.url} target="_blank" rel="noopener noreferrer" className="block">
        {inner}
      </a>
    );
  }
  return inner;
}

function FilterChip({
  active,
  state,
  onClick,
  title,
  children,
}: {
  active: boolean;
  state?: string;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Toggle
      pressed={active}
      onPressedChange={onClick}
      title={title}
      size="sm"
      className="h-6 px-1.5 rounded-[4px] border border-border data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary"
    >
      {children}
      {state && <span className="text-[9px] font-mono opacity-80 ml-0.5">{state}</span>}
    </Toggle>
  );
}

function MetricCard({
  label,
  value,
  pos,
  mono,
}: {
  label: string;
  value: string;
  pos?: boolean;
  mono?: boolean;
}) {
  return (
    <div className="border border-border rounded-[4px] px-2 py-1.5 bg-card">
      <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">
        {label}
      </div>
      <div
        className={cn(
          "text-[12px] mt-0.5 tabular-nums",
          mono ? "font-mono" : "font-semibold",
          pos === true && "text-[hsl(var(--chart-up))]",
          pos === false && "text-[hsl(var(--chart-down))]",
        )}
      >
        {value}
      </div>
    </div>
  );
}
